import React from 'react';
import { useMediaQuery, useTheme, Dialog, DialogTitle, DialogContent, DialogActions, Typography } from '@mui/material';
import { theme } from '../constants/theme';
import { FONT_ENUM } from '../types/global/fonts.types';
import { useThemeContext } from '../hook/useThemeManager';
import CustomButton from '../components/Button/CustomButton';

/**
 * Higher-order component that restricts a component to be used only on desktop devices.
 * On mobile devices, it shows a dialog indicating the feature is only available on desktop.
 */
export const withDesktopOnly = <P extends object>(Component: React.ComponentType<P>) => {
    const WithDesktopOnly: React.FC<
        P & {
            open?: boolean;
            onClose?: () => void;
            title?: string;
            message?: string;
        }
    > = props => {
        const { themeStyle } = useThemeContext();
        const muiTheme = useTheme();
        const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

        const {
            open,
            onClose,
            title = 'Desktop Only Feature',
            message = 'This feature is only available on desktop devices. Please use a larger screen to access it.',
        } = props;

        if (isMobile) {
            return (
                <Dialog
                    open={open || false}
                    onClose={onClose}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        style: {
                            backgroundColor: theme[themeStyle].form_background,
                            borderRadius: 8,
                            padding: 16,
                        },
                    }}>
                    <DialogTitle
                        sx={{
                            color: theme[themeStyle].primary_white,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                        }}>
                        {title}
                    </DialogTitle>
                    <DialogContent>
                        <Typography
                            sx={{
                                color: theme[themeStyle].primary_white,
                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                            }}>
                            {message}
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <CustomButton
                            label="Close"
                            onClick={onClose}
                            customStyle={{
                                borderRadius: '4px',
                            }}
                        />
                    </DialogActions>
                </Dialog>
            );
        }

        return <Component {...(props as P)} />;
    };

    return WithDesktopOnly;
};

export default withDesktopOnly;
